import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building, Calendar, MapPin, Phone, Mail, Lock, 
  Eye, EyeOff, Facebook, Instagram, CheckCircle2, 
  Link, Globe, CheckSquare, Shield, Camera, 
  Info, MessageSquare, Award, Clock, UserCheck, Star, Briefcase,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from '../ui/toast/use-toast';
import LocationAutocomplete from '../LocationAutocomplete';
import BaseRegister from '../register/BaseRegister';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: "easeOut" 
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const InmRegister = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const totalSteps = 7; // Reducido de 8 a 7 pasos

  // Estados
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // Estados de verificación
  const [verificationState, setVerificationState] = useState({
    loading: false,
    codeSent: false,
    error: null
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [devCode, setDevCode] = useState(null);

  // Datos del formulario
  const [formData, setFormData] = useState({
    userType: 'inmobiliaria',
    nombre: '',
    nombre_empresa: '',
    slug: '',
    ruc: '',
    fechaNacimiento: '',
    fotoPerfil: null,
    fotoPreview: null,
    ubicacion: '',
    ciudad: '',
    provincia: '',
    pais: '',
    email: '',
    emailVerified: false,
    telefono: '', // Mantenemos el campo pero sin validación
    prefijo_telefono: '+51',
    whatsapp: '',
    facebook: '',
    instagram: '',
    sitio_web: '',
    sobre_nosotros: '',
    experiencia: '',
    anos_experiencia: '',
    servicios: [],
    especialidades: [],
    password: '',
    confirmPassword: '',
    slogan: ''
  });

  // Lista de servicios disponibles para inmobiliarias
  const SERVICIOS_INMOBILIARIOS = [
    'Compra y venta de propiedades',
    'Alquiler de inmuebles',
    'Tasaciones profesionales',
    'Asesoría legal inmobiliaria',
    'Gestión de propiedades',
    'Marketing inmobiliario',
    'Inversiones inmobiliarias',
    'Financiamiento inmobiliario',
    'Administración de propiedades',
    'Servicios postventa'
  ];

  // Lista de especialidades
  const ESPECIALIDADES = [
    'Residencial',
    'Comercial',
    'Industrial',
    'Oficinas',
    'Terrenos',
    'Proyectos en construcción',
    'Propiedades de lujo',
    'Alquileres vacacionales'
  ];

  // Helper function for error handling fetch requests
  const handleFetchError = (error, defaultMessage = 'Error de conexión') => {
    console.error('Network Error:', error);
    
    const errorMessage = error.message === 'Failed to fetch' 
      ? defaultMessage 
      : error.message;

    toast({
      title: "Error de Conexión",
      description: errorMessage,
      variant: "destructive"
    });

    return null;
  };

  // Timer para reenvío de código
  useEffect(() => {
    let timer;
    if (timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else {
      setResendDisabled(false);
    }
    return () => clearInterval(timer);
  }, [timeLeft]);

  // Manejadores de cambios en formulario
  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpiar errores al cambiar el valor
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Validaciones específicas al cambiar el valor, excepto email
    switch (name) {
      case 'telefono':
        // Validación básica pero sin verificar disponibilidad
        if (value && value.length !== 9) {
          setErrors(prev => ({
            ...prev,
            telefono: 'El teléfono debe tener 9 dígitos'
          }));
        }
        break;

      case 'ruc':
        // Validar RUC peruano (11 dígitos)
        if (value && !/^\d{11}$/.test(value)) {
          setErrors(prev => ({
            ...prev,
            ruc: 'El RUC debe tener 11 dígitos'
          }));
        }
        break;

      case 'slug':
        // Validar slug (solo letras minúsculas, números y guiones)
        const slugValue = value.toLowerCase().replace(/[^a-z0-9-.]/g, '');
        setFormData(prev => ({
          ...prev,
          slug: slugValue
        }));
        break;

      case 'sitio_web':
        // Validar formato de URL
        const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
        if (value && !urlRegex.test(value)) {
          setErrors(prev => ({
            ...prev,
            sitio_web: 'Ingrese una URL válida'
          }));
        }
        break;
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, fotoPerfil: 'La imagen no debe superar 5MB' }));
        return;
      }

      // Mostrar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ 
          ...prev, 
          fotoPerfil: file,
          fotoPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);

      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.fotoPerfil;
        return newErrors;
      });
    }
  };

  const handleLocationSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      ubicacion: `${location.city}, ${location.province}, ${location.country}`,
      ciudad: location.city,
      provincia: location.province,
      pais: location.country
    }));
    
    if (errors.location) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.location;
        return newErrors;
      });
    }
  };

  const handleServiceChange = (service) => {
    setFormData(prev => {
      const newServices = prev.servicios.includes(service)
        ? prev.servicios.filter(s => s !== service)
        : [...prev.servicios, service];
      return { ...prev, servicios: newServices };
    });
  };

  const handleEspecialidadChange = (especialidad) => {
    setFormData(prev => {
      const newEspecialidades = prev.especialidades.includes(especialidad)
        ? prev.especialidades.filter(e => e !== especialidad)
        : [...prev.especialidades, especialidad];
      return { ...prev, especialidades: newEspecialidades };
    });
  };

  // Manejadores de verificación
  const requestVerificationCode = async () => {
    if (!formData.email) {
      setErrors(prev => ({ ...prev, email: 'El email es requerido' }));
      return;
    }

    // Validación básica de formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Formato de email inválido' }));
      return;
    }

    // Indicamos que estamos verificando
    setVerificationState({
      loading: true,
      codeSent: false,
      error: null
    });

    try {
      // Crear un nuevo AbortController para toda la operación
      const controller = new AbortController();
      
      // Configurar un timeout más largo
      const timeoutId = setTimeout(() => {
        controller.abort('La solicitud tardó demasiado');
      }, 15000); // 15 segundos

      // Verificar primero si el email ya existe
      const checkResponse = await fetch(`https://backend-production-1786.up.railway.app/api/auth/check-email/${formData.email.trim()}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        signal: controller.signal
      });

      if (!checkResponse.ok) {
        clearTimeout(timeoutId);
        const errorData = await checkResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al verificar email');
      }

      const checkData = await checkResponse.json();
      console.log('Respuesta de verificación de email:', checkData);
      
      // Si el email ya existe según la respuesta del servidor
      if (checkData.exists) {
        clearTimeout(timeoutId);
        setErrors(prev => ({
          ...prev,
          email: 'Este email ya está registrado'
        }));
        
        setVerificationState({
          loading: false,
          codeSent: false,
          error: 'Este email ya está registrado. Por favor utiliza otro email o inicia sesión.'
        });
        
        return;
      }

      console.log('Iniciando solicitud de código de verificación para:', formData.email);

      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/verification/request-email-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          userType: formData.userType
        }),
        signal: controller.signal
      });

      // Limpiar el timeout
      clearTimeout(timeoutId);

      console.log('Respuesta del servidor:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error en respuesta:', errorData);
        
        // Si el servidor indica que el email ya está registrado
        if (errorData.error === 'EMAIL_EXISTS' || 
            errorData.message?.includes('ya está registrado') || 
            response.status === 409) {
          throw new Error('Este email ya está registrado. Por favor utiliza otro email o inicia sesión.');
        } else {
          throw new Error(errorData.error || errorData.message || 'Error en la verificación');
        }
      }

      const data = await response.json();
      console.log('Código enviado correctamente');

      // Todo salió bien, actualizamos el estado
      setVerificationState({
        loading: false,
        codeSent: true,
        error: null
      });

      toast({
        title: "Código enviado",
        description: "Revisa tu correo electrónico para el código de verificación",
      });

      // Iniciar temporizador para reenvío
      setTimeLeft(600); // 10 minutos
      setResendDisabled(true);

      // En desarrollo, mostrar el código
      if (process.env.NODE_ENV === 'development' && data.code) {
        console.log('Código de verificación (desarrollo):', data.code);
        setDevCode(data.code);
      }

    } catch (error) {
      console.error('Error al solicitar código:', error.message);
      
      // Manejar específicamente errores de cancelación
      if (error.name === 'AbortError') {
        toast({
          title: "Tiempo de espera agotado",
          description: "La solicitud tardó demasiado. Por favor, verifica tu conexión e inténtalo de nuevo.",
          variant: "destructive"
        });
      } else {
        // Manejo detallado de errores de red
        const errorMessage = error.message || 'No se pudo enviar el código de verificación';
        
        // Si el mensaje indica que el email ya está registrado
        if (errorMessage.includes('ya está registrado')) {
          setErrors(prev => ({
            ...prev,
            email: 'Este email ya está registrado'
          }));
        }
        
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      }

      // Resetear estado de carga
      setVerificationState({
        loading: false,
        codeSent: false,
        error: error.message || 'Error al enviar código'
      });
    }
  };

  const verifyEmailCode = async (code) => {
    if (code.length !== 6) return;

    try {
      setVerificationState(prev => ({ ...prev, loading: true }));

      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/verification/verify-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          code: code,
          userType: formData.userType
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Código inválido');
      }

      const data = await response.json();

      setFormData(prev => ({ ...prev, emailVerified: true }));
      // Limpiar cualquier error relacionado con el email
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.email;
        return newErrors;
      });
      
      setVerificationState({
        loading: false,
        codeSent: false,
        error: null
      });

      toast({
        title: "Email verificado",
        description: "Tu email ha sido verificado correctamente",
      });

      handleNext();

    } catch (error) {
      console.error('Error en verificación:', error);
      setVerificationState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));

      toast({
        title: "Error",
        description: error.message || "Error al verificar el código",
        variant: "destructive",
      });
    }
  };

  // Funciones de navegación
  const validateStep = () => {
    const newErrors = {};

    switch (currentStep) {
      case 1: // Email verification
        if (!formData.email) {
          newErrors.email = 'El email es requerido';
        } else if (!formData.emailVerified) {
          newErrors.email = 'Debes verificar tu email antes de continuar';
        }
        break;

      case 2: // Password
        if (!formData.password) {
          newErrors.password = 'La contraseña es requerida';
        } else if (formData.password.length < 8) {
          newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
        }
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Las contraseñas no coinciden';
        }
        break;

      case 3: // Información de la empresa
        if (!formData.nombre) newErrors.nombre = 'El nombre es requerido';
        if (!formData.nombre_empresa) newErrors.nombre_empresa = 'La razón social es requerida';
        if (!formData.ruc) {
          newErrors.ruc = 'El RUC es requerido';
        } else if (!/^\d{11}$/.test(formData.ruc)) {
          newErrors.ruc = 'El RUC debe tener 11 dígitos';
        }
        if (!formData.slug) {
          newErrors.slug = 'El identificador único es requerido';
        } else if (!/^[a-z0-9-.]+$/.test(formData.slug)) {
          newErrors.slug = 'El identificador solo puede contener letras minúsculas, números, puntos y guiones';
        }
        break;

      case 4: // Ubicación y logo
        if (!formData.ubicacion) {
          newErrors.location = 'Por favor seleccione una ubicación válida';
        }
        if (!formData.fotoPerfil) newErrors.fotoPerfil = 'El logo de la inmobiliaria es requerido';
        break;

      case 5: // Información de contacto y redes
        if (!formData.sobre_nosotros) {
          newErrors.sobre_nosotros = 'La descripción de la empresa es requerida';
        }
        break;

      case 6: // Experiencia y slogan
        if (!formData.slogan) newErrors.slogan = 'El slogan es requerido';
        if (!formData.experiencia) newErrors.experiencia = 'La experiencia es requerida';
        if (!formData.anos_experiencia) {
          newErrors.anos_experiencia = 'Los años de experiencia son requeridos';
        }
        break;

      case 7: // Servicios, especialidades y términos (último paso)
        if (formData.servicios.length === 0) {
          newErrors.servicios = 'Debe seleccionar al menos un servicio';
        }
        if (formData.servicios.length > 6) {
          newErrors.servicios = 'No puede seleccionar más de 6 servicios';
        }
        if (formData.especialidades.length === 0) {
          newErrors.especialidades = 'Debe seleccionar al menos una especialidad';
        }
        
        // Validar teléfono (opcional pero si se proporciona debe ser válido)
        if (formData.telefono && formData.telefono.length !== 9) {
          newErrors.telefono = 'El teléfono debe tener 9 dígitos';
        }
        
        // Verificar aceptación de términos y políticas
        if (!acceptedTerms) {
          newErrors.terms = 'Debe aceptar los Términos y Condiciones';
        }
        if (!acceptedPrivacy) {
          newErrors.privacy = 'Debe aceptar la Política de Privacidad';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => prev + 1);
      setErrors({});
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    setErrors({});
  };

  const handleFinish = async () => {
    if (!validateStep()) return;

    setLoading(true);
    try {
      const formDataToSend = new FormData();
      
      // Preparar los datos
      Object.keys(formData).forEach(key => {
        if (key === 'fotoPerfil' && formData[key]) {
          formDataToSend.append(key, formData[key]);
        } else if (!['confirmPassword', 'fotoPreview'].includes(key)) {
          // Convertir arrays a JSON strings para servicios y especialidades
          if (Array.isArray(formData[key])) {
            formDataToSend.append(key, JSON.stringify(formData[key]));
          } else {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/register/inmobiliaria', {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error en el registro');
      }

      // Mostrar mensaje de éxito
      toast({
        title: "Registro exitoso",
        description: "¡Te has registrado correctamente! Por favor revisa tu correo electrónico para un mensaje de bienvenida.",
        variant: "success",
      });

      // Login automático
      if (data.token && data.user) {
        login(data.token, data.user);
        navigate('/mi_perfil');
      } else {
        throw new Error('Datos de autenticación inválidos');
      }
    } catch (error) {
      console.error('Error en registro:', error);
      setErrors({ submit: error.message });
      
      toast({
        title: "Error en el registro",
        description: error.message || "No se pudo completar el registro",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Componente para mostrar el estado de la verificación
  const VerificationStatus = () => {
    if (verificationState.loading) {
      return (
        <div className="flex items-center justify-center space-x-2 text-blue-500">
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
          <span>Enviando código...</span>
        </div>
      );
    }
    
    if (verificationState.error) {
      return (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0"/>
          <span>{verificationState.error}</span>
        </div>
      );
    }
    
    if (verificationState.codeSent) {
      return (
        <div className="bg-blue-50 text-blue-700 p-3 rounded-lg">
          <p>Te hemos enviado un código de verificación por email.</p>
          <p className="mt-1 text-sm">
            Tiempo restante: {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
          </p>
        </div>
      );
    }
    
    return null;
  };

  // Renderizado de pasos
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return renderEmailVerification();
      case 2:
        return renderPasswordStep();
      case 3:
        return renderCompanyInfo();
      case 4:
        return renderLocationAndLogo();
      case 5:
        return renderContactInfo();
      case 6:
        return renderExperienceAndSlogan();
      case 7:
        return renderServicesAndTerms(); // Nuevo paso combinado
      default:
        return null;
    }
  };

  // Renderizado de verificación de email
  const renderEmailVerification = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Verificación de Email
      </motion.h3>

      {formData.emailVerified ? (
        <motion.div variants={fadeInUp} className="bg-green-50 p-4 rounded-xl border border-green-200">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-green-500" />
            <div>
              <h4 className="font-medium text-green-800">Email verificado correctamente</h4>
              <p className="text-green-600 text-sm">{formData.email}</p>
            </div>
          </div>
        </motion.div>
      ) : (
        <motion.div variants={fadeInUp} className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
          <input
            type="email"
            name="email"
            placeholder="Email corporativo"
            value={formData.email}
            onChange={handleInputChange}
            className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl transition-colors duration-300 ${
              errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200'
            }`}
            disabled={verificationState.loading}
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email}</p>
          )}
        </motion.div>
      )}

      {!formData.emailVerified && (
        <>
          <motion.button
            variants={fadeInUp}
            onClick={requestVerificationCode}
            disabled={verificationState.loading || resendDisabled || !formData.email}
            whileHover={!verificationState.loading && !resendDisabled && formData.email ? { scale: 1.02 } : {}}
            whileTap={!verificationState.loading && !resendDisabled && formData.email ? { scale: 0.98 } : {}}
            className={`w-full py-3 rounded-xl transition-all duration-300
              ${verificationState.loading || resendDisabled || !formData.email
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md hover:shadow-lg'
              }`}
          >
            {verificationState.loading ? (
              <div className="flex items-center justify-center">
                <span className="mr-2">Enviando...</span>
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
              </div>
            ) : verificationState.codeSent ? (
              'Reenviar código'
            ) : (
              'Enviar código de verificación'
            )}
          </motion.button>

          <VerificationStatus />

          {verificationState.codeSent && (
            <motion.div 
              variants={fadeInUp} 
              className="mt-4 space-y-4"
            >
              <div className="bg-white p-4 rounded-xl border border-gray-200">
                <p className="text-sm text-gray-700 mb-3 text-center">
                  Ingresa el código de 6 dígitos que enviamos a tu email
                </p>
                <input
                  type="text"
                  placeholder="Código de verificación"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(value);
                    if (value.length === 6) {
                      verifyEmailCode(value);
                    }
                  }}
                  className="w-full px-4 py-3 border-2 rounded-xl text-center text-lg tracking-wider focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                  maxLength={6}
                />
              </div>
              {timeLeft > 0 && (
                <p className="text-sm text-gray-500 text-center">
                  Podrás reenviar el código en: <span className="font-medium">{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</span>
                </p>
              )}
            </motion.div>
          )}

          {/* Código en modo desarrollo */}
          {process.env.NODE_ENV === 'development' && devCode && verificationState.codeSent && (
            <motion.div 
              variants={fadeInUp}
              className="bg-indigo-50 border border-indigo-200 p-3 rounded-lg mt-4"
            >
              <p className="text-indigo-800 font-medium">Código de desarrollo: {devCode}</p>
              <p className="text-xs text-indigo-600">Este código solo se muestra en modo desarrollo</p>
            </motion.div>
          )}
        </>
      )}
      
      <motion.div 
        variants={fadeInUp}
        className="bg-indigo-50 rounded-xl p-4 border border-indigo-100"
      >
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-indigo-700">
            La verificación de su email corporativo nos ayuda a proteger su cuenta y a mantener segura nuestra plataforma. También nos permite enviarle notificaciones importantes sobre sus propiedades.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  // Renderizado del paso de contraseña
  const renderPasswordStep = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Configura tu Contraseña
      </motion.h3>

      <motion.div variants={fadeInUp} className="relative">
        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type={showPassword ? "text" : "password"}
          placeholder="Contraseña"
          name="password"
          value={formData.password}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-12 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.password ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-indigo-500 transition-colors"
        >
          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
        {errors.password && (
          <p className="text-sm text-red-500 mt-1">{errors.password}</p>
        )}
      </motion.div>

      <motion.div variants={fadeInUp} className="relative">
        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type={showConfirmPassword ? "text" : "password"}
          placeholder="Confirmar Contraseña"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-12 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        <button
          type="button"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-indigo-500 transition-colors"
        >
          {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
        {errors.confirmPassword && (
          <p className="text-sm text-red-500 mt-1">{errors.confirmPassword}</p>
        )}
      </motion.div>

      <motion.div variants={fadeInUp} className="space-y-3 mt-4 bg-white p-4 rounded-xl border border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <Shield className="w-4 h-4 text-indigo-500" />
          Requisitos de seguridad:
        </h4>
        <ul className="space-y-2">
          <li className={`flex items-center text-sm ${formData.password.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
            <div className={`flex-shrink-0 w-5 h-5 mr-2 rounded-full flex items-center justify-center ${
              formData.password.length >= 8 ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              <CheckCircle2 className={`w-4 h-4 ${formData.password.length >= 8 ? 'text-green-500' : 'text-gray-400'}`} />
            </div>
            Mínimo 8 caracteres
          </li>
          <li className={`flex items-center text-sm ${/[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
            <div className={`flex-shrink-0 w-5 h-5 mr-2 rounded-full flex items-center justify-center ${
              /[A-Z]/.test(formData.password) ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              <CheckCircle2 className={`w-4 h-4 ${/[A-Z]/.test(formData.password) ? 'text-green-500' : 'text-gray-400'}`} />
            </div>
            Al menos una mayúscula
          </li>
          <li className={`flex items-center text-sm ${/[0-9]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
            <div className={`flex-shrink-0 w-5 h-5 mr-2 rounded-full flex items-center justify-center ${
              /[0-9]/.test(formData.password) ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              <CheckCircle2 className={`w-4 h-4 ${/[0-9]/.test(formData.password) ? 'text-green-500' : 'text-gray-400'}`} />
            </div>
            Al menos un número
          </li>
        </ul>
      </motion.div>
      
      <motion.div 
        variants={fadeInUp}
        className="bg-indigo-50 rounded-xl p-4 border border-indigo-100"
      >
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-indigo-700">
            Una contraseña segura es fundamental para proteger su cuenta. Nunca comparta su contraseña con nadie y evite reutilizar contraseñas de otros sitios.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  // Componentes de renderizado de pasos
  const renderCompanyInfo = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Información de la Inmobiliaria
      </motion.h3>

      <motion.div variants={fadeInUp} className="relative">
        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="text"
          placeholder="Nombre Comercial"
          name="nombre"
          value={formData.nombre}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.nombre ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.nombre && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.nombre}</p>
        )}
      </motion.div>

      <motion.div variants={fadeInUp} className="relative">
        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="text"
          placeholder="Razón Social"
          name="nombre_empresa"
          value={formData.nombre_empresa}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.nombre_empresa ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.nombre_empresa && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.nombre_empresa}</p>
        )}
      </motion.div>

      <motion.div variants={fadeInUp} className="relative">
        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="text"
          placeholder="RUC"
          name="ruc"
          value={formData.ruc}
          onChange={handleInputChange}
          maxLength="11"
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.ruc ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.ruc && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.ruc}</p>
        )}
      </motion.div>

      <motion.div variants={fadeInUp} className="relative">
        <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="text"
          placeholder="Identificador único (ejemplo: inmobiliaria-lima)"
          name="slug"
          value={formData.slug}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.slug ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.slug && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.slug}</p>
        )}
        <p className="text-xs text-gray-500 mt-1 ml-2">
          Este será tu URL: www.inkayros.com/inmobiliaria/{formData.slug}
        </p>
      </motion.div>

      <motion.div variants={fadeInUp} className="relative">
        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="date"
          name="fechaNacimiento"
          value={formData.fechaNacimiento}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.fechaNacimiento ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.fechaNacimiento && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.fechaNacimiento}</p>
        )}
        <p className="text-xs text-gray-500 mt-1 ml-2">
          Fecha de fundación de la inmobiliaria
        </p>
      </motion.div>

      <motion.div 
        variants={fadeInUp}
        className="bg-blue-50 rounded-xl p-4 border border-blue-100 mt-4"
      >
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700">
            Esta información será utilizada para su perfil público y documentos legales. Asegúrese de que los datos sean correctos y estén actualizados.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  const renderLocationAndLogo = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Ubicación y Logo
      </motion.h3>

      {/* Logo de la inmobiliaria */}
      <motion.div variants={fadeInUp} className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Logo de la Inmobiliaria
        </label>
        <div className="flex items-center space-x-4">
          <div className={`h-20 w-20 rounded-full flex items-center justify-center border-2 overflow-hidden ${
            formData.fotoPreview ? 'border-indigo-300' : errors.fotoPerfil ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
          }`}>
            {formData.fotoPreview ? (
              <img src={formData.fotoPreview} alt="Preview" className="h-full w-full object-cover" />
            ) : (
              <Camera className="h-6 w-6 text-gray-400" />
            )}
          </div>
          <div className="flex-1">
            <label className="cursor-pointer">
              <div className="px-4 py-2 text-sm bg-indigo-50 text-indigo-600 rounded-lg border border-indigo-200 hover:bg-indigo-100 transition-colors text-center">
                {formData.fotoPerfil ? 'Cambiar logo' : 'Seleccionar logo'}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
            {errors.fotoPerfil && (
              <p className="mt-1 text-sm text-red-500">{errors.fotoPerfil}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Formatos: JPG, PNG, WEBP (máx. 5MB)
            </p>
          </div>
        </div>
      </motion.div>

      {/* Ubicación */}
      <motion.div variants={fadeInUp} className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
          <MapPin className="h-4 w-4 text-indigo-500" />
          Ubicación de la Sede Principal
        </label>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
          <LocationAutocomplete 
            onLocationSelect={handleLocationSelect}
            placeholder="Buscar ubicación..."
            className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
              errors.location ? 'border-red-300 bg-red-50' : 'border-gray-200'
            }`}
          />
          {errors.location && (
            <p className="mt-1 text-sm text-red-500 ml-2">{errors.location}</p>
          )}
        </div>
      </motion.div>
      
      <motion.div 
        variants={fadeInUp}
        className="bg-blue-50 rounded-xl p-4 border border-blue-100 mt-4"
      >
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700">
            La ubicación de su sede principal ayudará a los clientes a encontrarle fácilmente. Su logo será visible en su perfil y en todas sus publicaciones.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  // Renderizado de información de contacto
  const renderContactInfo = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Información de Contacto <span className="text-sm font-normal text-gray-500">(Opcional)</span>
      </motion.h3>

      <motion.div variants={fadeInUp} className="relative">
        <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="url"
          placeholder="Sitio Web (ejemplo: https://www.inmobiliaria.com)"
          name="sitio_web"
          value={formData.sitio_web}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.sitio_web ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.sitio_web && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.sitio_web}</p>
        )}
      </motion.div>

      <motion.div variants={fadeInUp} className="relative">
        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <div className="flex">
          <div className="bg-indigo-50 flex items-center px-3 border-2 border-r-0 border-gray-200 rounded-l-xl">
            <span className="text-gray-600 text-sm">+51</span>
          </div>
          <input
            type="tel"
            placeholder="WhatsApp de ventas"
            name="whatsapp"
            value={formData.whatsapp}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 9);
              setFormData(prev => ({ ...prev, whatsapp: value }));
            }}
            className="w-full pl-4 pr-4 py-3 border-2 border-gray-200 rounded-r-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      </motion.div>

      <motion.div variants={fadeInUp} className="relative">
        <Facebook className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="text"
          placeholder="Página de Facebook"
          name="facebook"
          value={formData.facebook}
          onChange={handleInputChange}
          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </motion.div>

      <motion.div variants={fadeInUp} className="relative">
        <Instagram className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="text"
          placeholder="Cuenta de Instagram"
          name="instagram"
          value={formData.instagram}
          onChange={handleInputChange}
          className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </motion.div>

      <motion.div variants={fadeInUp} className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-indigo-500" />
          Sobre Nosotros
        </label>
        <textarea
          name="sobre_nosotros"
          value={formData.sobre_nosotros}
          onChange={handleInputChange}
          placeholder="Describe la historia y valores de tu inmobiliaria..."
          className={`w-full p-4 border-2 rounded-xl min-h-[120px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.sobre_nosotros ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
          maxLength={500}
        />
        {errors.sobre_nosotros && (
          <p className="text-sm text-red-500 mt-1">{errors.sobre_nosotros}</p>
        )}
        <p className="text-xs text-gray-500 text-right">
          {formData.sobre_nosotros.length}/500 caracteres
        </p>
      </motion.div>
      
      <motion.div 
        variants={fadeInUp}
        className="bg-blue-50 rounded-xl p-4 border border-blue-100"
      >
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700">
            Compartir sus redes sociales y sitio web aumentará su visibilidad y facilitará que los clientes se pongan en contacto con ustedes por diferentes canales.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  // Renderizado de experiencia y eslogan
  const renderExperienceAndSlogan = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Experiencia Profesional
      </motion.h3>

      <motion.div variants={fadeInUp} className="relative">
        <label className="block text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
          <Star className="h-4 w-4 text-indigo-500" />
          Slogan
        </label>
        <input
          type="text"
          placeholder="Ej: Tu hogar ideal está aquí"
          name="slogan"
          value={formData.slogan}
          onChange={handleInputChange}
          className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.slogan ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
          maxLength={100}
        />
        <div className="flex justify-between mt-1">
          <span className={`text-xs ${formData.slogan.length > 80 ? 'text-amber-500' : 'text-gray-500'}`}>
            {formData.slogan.length}/100 caracteres
          </span>
          {errors.slogan && (
            <p className="text-sm text-red-500">{errors.slogan}</p>
          )}
        </div>
      </motion.div>

      <motion.div variants={fadeInUp} className="relative">
        <label className="block text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
          <Clock className="h-4 w-4 text-indigo-500" />
          Años de Experiencia
        </label>
        <input
          type="number"
          name="anos_experiencia"
          value={formData.anos_experiencia}
          onChange={handleInputChange}
          min="0"
          max="100"
          className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.anos_experiencia ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.anos_experiencia && (
          <p className="text-sm text-red-500 mt-1">{errors.anos_experiencia}</p>
        )}
      </motion.div>

      <motion.div variants={fadeInUp} className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
          <Briefcase className="h-4 w-4 text-indigo-500" />
          Descripción de su Experiencia
        </label>
        <textarea
          name="experiencia"
          value={formData.experiencia}
          onChange={handleInputChange}
          placeholder="Detalle la trayectoria y experiencia de su inmobiliaria en el sector..."
          className={`w-full p-4 border-2 rounded-xl min-h-[120px] focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.experiencia ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.experiencia && (
          <p className="text-sm text-red-500 mt-1">{errors.experiencia}</p>
        )}
      </motion.div>
      
      <motion.div 
        variants={fadeInUp}
        className="bg-indigo-50 rounded-xl p-4 border border-indigo-100"
      >
        <div className="flex items-start gap-3">
          <Award className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-indigo-700">
            La experiencia de su inmobiliaria es un factor importante para generar confianza. Los clientes valoran trabajar con empresas de trayectoria comprobada en el sector.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  // Renderizado de servicios, especialidades y términos (paso final combinado)
  const renderServicesAndTerms = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Servicios y Finalización
      </motion.h3>

      {/* Servicios */}
      <motion.div variants={fadeInUp} className="space-y-3">
        <h4 className="text-md font-medium text-gray-700">
          Servicios Ofrecidos <span className="text-sm font-normal text-gray-500">(Máximo 6)</span>
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SERVICIOS_INMOBILIARIOS.map((servicio) => (
            <motion.label
              key={servicio}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`flex items-center p-3 border-2 rounded-xl cursor-pointer transition-colors ${
                formData.servicios.includes(servicio) 
                  ? 'border-indigo-500 bg-indigo-50 shadow-sm' 
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={formData.servicios.includes(servicio)}
                onChange={() => handleServiceChange(servicio)}
                className="hidden"
              />
              <div className={`flex-shrink-0 p-1 rounded-full mr-3 ${
                formData.servicios.includes(servicio) ? 'bg-indigo-100' : 'bg-gray-100'
              }`}>
                <CheckSquare
                  className={`w-5 h-5 ${
                    formData.servicios.includes(servicio) 
                      ? 'text-indigo-600' 
                      : 'text-gray-400'
                  }`}
                />
              </div>
              <span className={`text-sm ${formData.servicios.includes(servicio) ? 'font-medium text-indigo-700' : 'text-gray-700'}`}>
                {servicio}
              </span>
            </motion.label>
          ))}
        </div>
        {errors.servicios && (
          <p className="text-red-500 text-sm mt-2">{errors.servicios}</p>
        )}
      </motion.div>

      {/* Especialidades */}
      <motion.div variants={fadeInUp} className="space-y-3 mt-6">
        <h4 className="text-md font-medium text-gray-700">
          Especialidades
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ESPECIALIDADES.map((especialidad) => (
            <motion.label
              key={especialidad}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`flex items-center p-3 border-2 rounded-xl cursor-pointer transition-colors ${
                formData.especialidades.includes(especialidad) 
                  ? 'border-indigo-500 bg-indigo-50 shadow-sm' 
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={formData.especialidades.includes(especialidad)}
                onChange={() => handleEspecialidadChange(especialidad)}
                className="hidden"
              />
              <div className={`flex-shrink-0 p-1 rounded-full mr-3 ${
                formData.especialidades.includes(especialidad) ? 'bg-indigo-100' : 'bg-gray-100'
              }`}>
                <CheckSquare
                  className={`w-5 h-5 ${
                    formData.especialidades.includes(especialidad) 
                      ? 'text-indigo-600' 
                      : 'text-gray-400'
                  }`}
                />
              </div>
              <span className={`text-sm ${formData.especialidades.includes(especialidad) ? 'font-medium text-indigo-700' : 'text-gray-700'}`}>
                {especialidad}
              </span>
            </motion.label>
          ))}
        </div>
        {errors.especialidades && (
          <p className="text-red-500 text-sm mt-2">{errors.especialidades}</p>
        )}
      </motion.div>

      {/* Teléfono opcional */}
      <motion.div variants={fadeInUp} className="space-y-3 mt-6">
        <h4 className="text-md font-medium text-gray-700">
          Información de Contacto <span className="text-sm font-normal text-gray-500">(Opcional)</span>
        </h4>
        <div className="flex gap-2">
          <select
            value={formData.prefijo_telefono}
            onChange={(e) => setFormData(prev => ({ ...prev, prefijo_telefono: e.target.value }))}
            className="pl-2 pr-8 py-3 border-2 rounded-xl bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
          >
            <option value="+51">🇵🇪 +51</option>
            <option value="+56">🇨🇱 +56</option>
            <option value="+57">🇨🇴 +57</option>
            <option value="+54">🇦🇷 +54</option>
            <option value="+593">🇪🇨 +593</option>
          </select>

          <div className="relative flex-1">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
            <input
              type="tel"
              placeholder="Número de teléfono (opcional)"
              name="telefono"
              value={formData.telefono}
              onChange={(e) => {
                // Permitir solo números y limitar a 9 dígitos
                const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                setFormData(prev => ({ 
                  ...prev, 
                  telefono: value 
                }));
                
                // Limpiar errores
                if (errors.telefono) {
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.telefono;
                    return newErrors;
                  });
                }
              }}
              className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl transition-colors duration-300 ${
                errors.telefono ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
            />
          </div>
        </div>
        {errors.telefono && (
          <p className="text-red-500 text-sm bg-red-50 p-2 rounded-lg">
            {errors.telefono}
          </p>
        )}
        <p className="text-xs text-gray-500">
          El teléfono es opcional. Si lo proporciona, debe tener 9 dígitos válidos.
        </p>
      </motion.div>

      {/* Términos y condiciones */}
      <motion.div 
        variants={fadeInUp}
        className="space-y-4 mt-6 bg-gray-50 p-6 rounded-xl border border-gray-200"
      >
        <h4 className="font-medium text-gray-800">Términos Legales</h4>
        <p className="text-sm text-gray-600">
          Para completar tu registro, es necesario que aceptes nuestros términos legales:
        </p>
        
        <div className="space-y-3">
          {/* Checkbox para Términos y Condiciones */}
          <div className={`flex items-start gap-3 ${errors.terms ? 'pb-1' : ''}`}>
            <div className="flex items-center h-5 mt-1">
              <input
                id="terms"
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => {
                  setAcceptedTerms(e.target.checked);
                  if (errors.terms && e.target.checked) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.terms;
                      return newErrors;
                    });
                  }
                }}
                className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${
                  errors.terms ? 'border-red-300' : ''
                }`}
              />
            </div>
            <div>
              <label htmlFor="terms" className={`text-sm font-medium ${errors.terms ? 'text-red-700' : 'text-gray-700'}`}>
                Acepto los <a 
                  href="/terminos" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-indigo-600 hover:text-indigo-800 hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('/terminos', '_blank');
                  }}
                >
                  Términos y Condiciones
                </a>
              </label>
              {errors.terms && (
                <p className="text-sm text-red-600">{errors.terms}</p>
              )}
            </div>
          </div>
          
          {/* Checkbox para Política de Privacidad */}
          <div className={`flex items-start gap-3 ${errors.privacy ? 'pb-1' : ''}`}>
            <div className="flex items-center h-5 mt-1">
              <input
                id="privacy"
                type="checkbox"
                checked={acceptedPrivacy}
                onChange={(e) => {
                  setAcceptedPrivacy(e.target.checked);
                  if (errors.privacy && e.target.checked) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.privacy;
                      return newErrors;
                    });
                  }
                }}
                className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${
                  errors.privacy ? 'border-red-300' : ''
                }`}
              />
            </div>
            <div>
              <label htmlFor="privacy" className={`text-sm font-medium ${errors.privacy ? 'text-red-700' : 'text-gray-700'}`}>
                Acepto la <a 
                  href="/privacidad" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-indigo-600 hover:text-indigo-800 hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('/privacidad', '_blank');
                  }}
                >
                  Política de Privacidad
                </a>
              </label>
              {errors.privacy && (
                <p className="text-sm text-red-600">{errors.privacy}</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="text-xs text-gray-500 pt-2">
          Al aceptar, confirmas que has leído y estás de acuerdo con nuestros términos legales y entiendes cómo procesamos tus datos.
        </div>
      </motion.div>
      
      <motion.div 
        variants={fadeInUp}
        className="bg-indigo-50 rounded-xl p-4 border border-indigo-100 mt-4"
      >
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-indigo-700">
            Los servicios y especialidades que seleccione aparecerán en su perfil público y ayudarán a los clientes a entender su oferta inmobiliaria completa.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <BaseRegister
      currentStep={currentStep}
      totalSteps={totalSteps}
      handleNext={handleNext}
      handleBack={handleBack}
      handleFinish={handleFinish}
      loading={loading}
      title="Registro de Inmobiliaria"
      errors={errors}
    >
      {renderStep()}
    </BaseRegister>
  );
};

export default InmRegister;